import { createContext, useContext, useRef, useCallback } from "react"
import type { PortfolioItem } from "@/types/models"

interface TattooItemRef {
  type: "flash" | "portfolioItem" | "healedPortfolioItem"
  id: string
  score?: number
}

export interface FeedCacheEntry {
  items: PortfolioItem[]
  allLeftItems: TattooItemRef[]
  allRightItems: TattooItemRef[]
  loadedItemIds: Set<string>
  currentLoadedCount: number
  searchScores?: Map<string, number>
  cachedAt: number
  scrollPosition: number
}

interface FeedCacheContextValue {
  getEntry: (cacheKey: string) => FeedCacheEntry | null
  setEntry: (cacheKey: string, entry: FeedCacheEntry) => void
  updateScrollPosition: (cacheKey: string, scrollY: number) => void
  clearEntry: (cacheKey: string) => void
}

const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

const FeedCacheContext = createContext<FeedCacheContextValue | null>(null)

export function FeedCacheProvider({ children }: { children: React.ReactNode }) {
  const cacheRef = useRef<Map<string, FeedCacheEntry>>(new Map())

  const getEntry = useCallback((cacheKey: string): FeedCacheEntry | null => {
    const entry = cacheRef.current.get(cacheKey)
    if (!entry) return null
    if (Date.now() - entry.cachedAt > CACHE_TTL_MS) {
      cacheRef.current.delete(cacheKey)
      return null
    }
    return entry
  }, [])

  const setEntry = useCallback((cacheKey: string, entry: FeedCacheEntry) => {
    cacheRef.current.set(cacheKey, entry)
  }, [])

  const updateScrollPosition = useCallback((cacheKey: string, scrollY: number) => {
    const entry = cacheRef.current.get(cacheKey)
    if (entry) {
      entry.scrollPosition = scrollY
    }
  }, [])

  const clearEntry = useCallback((cacheKey: string) => {
    cacheRef.current.delete(cacheKey)
  }, [])

  return (
    <FeedCacheContext.Provider value={{ getEntry, setEntry, updateScrollPosition, clearEntry }}>
      {children}
    </FeedCacheContext.Provider>
  )
}

export function useFeedCache() {
  const context = useContext(FeedCacheContext)
  if (!context) {
    throw new Error("useFeedCache must be used within FeedCacheProvider")
  }
  return context
}
